import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from '../../../../../context/store';
import shallow from 'zustand/shallow';
import { Panel } from '../Panel';
import { ProjectPicker } from './ProjectPicker';
import { useLocation, useMatch } from 'react-router';
import { getRouteFromEnum, MainView } from '../../../../../Entities/MainView';
import { IconButton, IPanelHeaderRenderer, IPanelProps, PanelType } from '@fluentui/react';
import { PROJECTS_KEY } from '../../../../../context/network/http/QueryProvider/queryKeys';
import { getGridTypeFromRoute, GridType } from '../../../../../Entities/GridType';
import { useQuery } from '@tanstack/react-query';
import { ApiCalls } from '../../../../../api/api';
import { DateResolution, GetProjectOwnerProjectsQuery, ProjectListItem } from '../../../../../api/generated/data-contracts';
import EnumEx from '../../../../../Utilities/EnumEx';
import { useTranslation } from '../../../../../Locales/i18n/localizationService';

export const ProjectPanel: React.FC = () => {
	const { t } = useTranslation();
    const { setUIContext, showProjectPicker, timelineEnd, timelineStart, timelineResolution } = useStore(
        store => ({
            showProjectPicker: store.ui.showProjectPicker,
            setUIContext: store.setUIContext,
            timelineEnd: store.timeline.end,
            timelineStart: store.timeline.start,
            timelineResolution: store.timeline.resolution,
        }),
        shallow,
    );
    const { pathname } = useLocation();
	// const { data: uiSettings } = useUISettings();

    const gridType = useMemo(() => {
        return getGridTypeFromRoute(pathname);
    }, [pathname]);
    const canShow = useMemo(() => {
        const _canShow: boolean = Boolean(
            EnumEx.equalsAny(gridType, GridType.Allocation, GridType.Request, GridType.LmCapacityProjects) &&
                timelineEnd &&
                timelineStart &&
                Object.values(DateResolution).includes(timelineResolution),
        );
        return _canShow;
    }, [gridType, timelineEnd, timelineResolution, timelineStart]);

    useEffect(() => {
        if (showProjectPicker && !canShow) {
            setUIContext({ showProjectPicker: false });
        }
    }, [canShow, setUIContext, showProjectPicker]);

    useEffect(() => {
        if (canShow) {
            const handler = (e: KeyboardEvent) => {
                if (!(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) && e.key.toLowerCase() === 'a') {
                    setUIContext(ui => ({ showProjectPicker: !ui.showProjectPicker }));
                }
            };
            window.addEventListener('keyup', handler);
            return () => {
                window.removeEventListener('keyup', handler);
            };
        }
    }, [canShow, setUIContext]);
    const isCurrentlyOnPlanner = useMatch(getRouteFromEnum(MainView.LmCapacity));
    const [panelSide, setPanelSide] = useState(PanelType.smallFixedNear);
    const toggleSide = useCallback(
        () => setPanelSide(panelSide => (panelSide === PanelType.smallFixedNear ? PanelType.smallFixedFar : PanelType.smallFixedNear)),
        [],
    );

    const [isDragging, setIsDragging] = useState(false);
    const [showAll, setShowAll] = useState(false);

    const PanelHeader = useCallback(
        (props?: IPanelProps, defaultRender?: IPanelHeaderRenderer) => {
            return (
                <>
                    {/* {Store.Get(_ => _.MainView == MainView.LmCapacity) && ( */}
                    {isCurrentlyOnPlanner && (
                        <IconButton
                            title={t("ProjectPicker.HeaderContent")}
                            iconProps={{ iconName: 'Switch' }}
                            styles={{ root: { right: '45px', position: 'absolute' } }}
                            onClick={toggleSide}
                        />
                    )}
                    {defaultRender?.(props)}
                </>
            );
        },
        [isCurrentlyOnPlanner, toggleSide, t],
    );

    const queryKey = useMemo(
        () => [
            PROJECTS_KEY,
            {
                gridType,
                // end: DateEx.specifyUtc(timelineEnd),
                // start: DateEx.specifyUtc(timelineStart),
                end: timelineEnd,
                start: timelineStart,
                selectAll: showAll,
                timelineResolution,
            },
        ],
        [gridType, showAll, timelineEnd, timelineResolution, timelineStart],
    );

    const { data, isFetching, isFetched } = useQuery({
        queryKey: queryKey,
        queryFn: async () => {
            try {
				const query: GetProjectOwnerProjectsQuery = {
					start: timelineStart,
					end: timelineEnd,
					resolution: timelineResolution,
					disableFiltering: showAll
				}

				if (gridType === GridType.Request) {
					return await ApiCalls.getProjectOwnerProjects(query)
						.then(res => res.data);
				} else {
					return await ApiCalls.getLineManagerProjects(query)
						.then(res => res.data);
				}
            } catch (error) {
                // The error is already handled before this
                return [];
            }
        },
        placeholderData: useRef([] as ProjectListItem[]).current,
        enabled: canShow && showProjectPicker,
    });

    return (
        <Panel
            isOpen={showProjectPicker}
            isLightDismiss={true}
            onDismiss={() => setUIContext({ showProjectPicker: false })}
            headerText={t("ProjectPicker.BodyHeaderText")}
            closeButtonAriaLabel={t("ProjectPicker.CloseButtonAreaLabel")}
            className="tp-panel projects"
            styles={{
                root: {
                    '.ms-Panel-contentInner, .ms-Panel-scrollableContent': { height: '100%', display: 'flex', flexDirection: 'column' },
                    '.ms-Panel-content': { flexGrow: 1, paddingRight: 0, paddingBottom: 0 },
                    // opacity: 0.3,
                    // pointerEvents: "none",
                    opacity: isDragging ? 0.3 : 1,
                    transition: 'opacity .3s',
                },
            }}
            isBlocking={!isCurrentlyOnPlanner}
            onRenderHeader={PanelHeader}
            type={isCurrentlyOnPlanner ? panelSide : PanelType.smallFixedNear}
        >
            <ProjectPicker
                setIsDragging={setIsDragging}
                data={data}
                gridType={gridType}
                isFetching={isFetching}
                queryKey={queryKey}
                setShowAll={setShowAll}
                showAll={showAll}
                isFetched={isFetched}
            />
        </Panel>
    );
};
